import React, { useCallback } from "react";
import type {
  BaseEntity,
  EntityHooks,
  PaginationState,
  SerializedViewState,
  SimpleTableConfig,
} from "../../../UniversalCRMTable/types";
import type { ExportScope, ExportFormat } from "../../types";
import type {
  EntityState,
  ExportState,
  FilterState,
  Handlers,
  PaginationModeContextValue,
} from "../../contexts/types";
import type { EntityPageQueryData } from "../useEntityPageMemoizedState";
import { useUniversalEntityPageHandlers } from "../useUniversalEntityPageHandlers";

interface CompositionHandlersDataState<T extends BaseEntity> {
  data: EntityPageQueryData<T> | null | undefined;
  refetch: () => void;
}

/**
 * Output shape: caller composes the final `Handlers<T>` by adding
 * `handleOpenDetailModal` (produced separately by useCompositionDetailModal).
 * `paginationHandlers` are returned at the top level because they feed
 * useCompositionCallbacks directly, not the context value.
 */
export interface CompositionHandlersResult<T extends BaseEntity> {
  handlers: Omit<Handlers<T>, "handleOpenDetailModal">;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
}

/**
 * Composition hook for Event Handlers
 * Assembles and wraps event handlers for the entity page
 *
 * Job Duty: Assemble and wrap event handlers
 */
export function useCompositionHandlers<T extends BaseEntity>(
  entityName: string,
  config: SimpleTableConfig<T>,
  hooks: EntityHooks<T>,
  isMobile: boolean,
  canCreate: boolean,
  paginationModeState: PaginationModeContextValue,
  dataState: CompositionHandlersDataState<T>,
  entityState: EntityState<T> & ExportState,
  filterState: FilterState<T>,
  normalizedCurrentFilters: SerializedViewState,
  filterHelpers: unknown
): CompositionHandlersResult<T> {
  // Assemble handlers
  const rawHandlers = useUniversalEntityPageHandlers({
    entityConfig: {
      entityName,
      config,
      hooks,
    },
    uiState: {
      isMobile,
      canCreate,
    },
    dataState: {
      currentPage: paginationModeState.paginationMode.page,
      setCurrentPage: paginationModeState.setPage,
      data: dataState.data,
      setPagination: ((
        update:
          | PaginationState
          | ((prev: PaginationState) => PaginationState)
      ) => {
        // Handle both object and state updater function patterns. The
        // upstream PaginationModeContextValue tracks page/pageSize/cursor
        // separately, so we synthesize a PaginationState snapshot for the
        // updater form and fan out to the underlying setters.
        const current: PaginationState = {
          page: paginationModeState.paginationMode.page,
          pageSize: paginationModeState.paginationMode.pageSize,
          cursor: paginationModeState.paginationMode.cursor,
        };
        const pagination = typeof update === "function" ? update(current) : update;

        if (pagination.page !== undefined) {
          paginationModeState.setPage(pagination.page);
        }
        if (pagination.pageSize !== undefined) {
          paginationModeState.setPageSize(pagination.pageSize);
        }
        if (pagination.cursor !== undefined) {
          paginationModeState.setCursor(pagination.cursor);
        }
      }),
      refetch: dataState.refetch,
    },
    entityState: {
      editingItem: entityState.editingItem,
      setEditingItem: entityState.setEditingItem,
      deletingItem: entityState.deletingItem,
      setDeletingItem: entityState.setDeletingItem,
    },
    filterState: {
      filtersManuallyChanged: false,
      setSerializeFilters: () => {},
      setApplyFilters: () => {},
      setCurrentFilters: filterState.setCurrentFilters,
      setAdvancedFilters: filterState.setAdvancedFilters,
      setFilterOperator: filterState.setFilterOperator,
      setTableFilters: filterState.setTableFilters,
      debouncedSearchTerm: filterState.debouncedSearchTerm,
      customFilters: filterState.tableFilters,
      currentFilters: normalizedCurrentFilters,
      advancedFilters: filterState.advancedFilters,
      filterHelpers,
    },
    exportState: {
      isFetchingRef: React.useRef(false),
      setExportProgress: entityState.setExportProgress,
      setShowExportModal: entityState.setShowExportModal,
      setIsExporting: entityState.setIsExporting,
    },
  });

  const {
    handleCreateItem,
    handleEditItem,
    handleInlineEdit,
    handleDeleteItem,
    handleViewItem,
    handleItemCreated,
    handleItemUpdated,
    handleItemDeleted,
    handleImportItems,
    handleCancelDelete,
    handleExportWithScope: handleExportWithScopeRaw,
    handleExportItems,
    handleSavedViewsReady,
    handlePageChange,
    handlePageSizeChange,
  } = rawHandlers;

  // Type-safe wrapper for handleExportWithScope — narrows the upstream
  // ExportScope/ExportFormat enums to the string-typed Handlers<T> contract.
  const handleExportWithScope = useCallback(
    (scope: string, format: string) => {
      return handleExportWithScopeRaw(scope as ExportScope, format as ExportFormat);
    },
    [handleExportWithScopeRaw]
  );

  const handleComposeMessage = useCallback(() => {
    entityState.setShowComposeModal(true);
    // Use the specific setter, not the entire entityState object, so this
    // callback does not recreate when other entityState fields change.
  }, [entityState.setShowComposeModal]);

  // Build a Handlers<T>-shaped object (minus handleOpenDetailModal, which the
  // caller adds from useCompositionDetailModal). This replaces the prior
  // pattern of spreading the entire `rawHandlers` (which leaked orchestration
  // internals like paginationHandlers, crudHandlers, loadMoreFunction, etc.
  // into the context value).
  const handlers: Omit<Handlers<T>, "handleOpenDetailModal"> = {
    handleCreateItem,
    handleEditItem,
    handleInlineEdit,
    handleDeleteItem,
    handleViewItem,
    handleItemCreated,
    handleItemUpdated,
    handleItemDeleted,
    handleImportItems,
    handleCancelDelete,
    handleExportWithScope,
    handleExportItems,
    handleSavedViewsReady,
    handleComposeMessage,
  };

  return {
    handlers,
    handlePageChange,
    handlePageSizeChange,
  };
}
